<template>
  <IdentityShell top-link="sign-in">
    <div class="signup-page">
      <IdentitySignupProgress
        :label="$t('auth.identity.signupPage.yourAccount', 'Your account')"
        :step="1"
        :total="3"
      />

      <h1 class="section-headline signup-page__title">
        {{ $t('auth.identity.signupPage.letsCreateYour', "Let's create your") }}
        <span class="highlight">{{
          $t('auth.identity.signupPage.accountHighlight', 'account')
        }}</span
        >{{ $t('auth.identity.signupPage.accountTrail', '.') }}
      </h1>

      <AlertDanger v-if="firstError" show class="mb-3">
        {{ firstError }}
      </AlertDanger>

      <template v-if="googleAvailable">
        <AuthSocialButtons
          mode="signup"
          intent="register"
          class="signup-page__social"
        />
        <div class="auth-divider signup-page__divider" aria-hidden="true">
          <span class="auth-divider__label">
            {{
              $t('auth.identity.signupPage.orUseYourEmail', 'or use your email')
            }}
          </span>
        </div>
      </template>

      <FormBase class="auth-form signup-page__form" @submit.prevent="onSubmit">
        <FormGroupBase
          class="mb-3"
          :label="$t('auth.identity.signupPage.yourNameLabel', 'Your name')"
          :state="fieldState('name')"
          :invalid-feedback="fieldError('name')"
        >
          <InputText
            v-model="name"
            autofocus
            autocomplete="name"
            :state="fieldState('name')"
          />
        </FormGroupBase>

        <FormGroupBase
          class="mb-3"
          :label="
            $t(
              'auth.identity.signupPage.organizationNameLabel',
              'Organization name'
            )
          "
          :state="fieldState('organization_name')"
          :invalid-feedback="fieldError('organization_name')"
        >
          <InputText
            v-model="organizationName"
            autocomplete="organization"
            :placeholder="
              $t(
                'auth.identity.signupPage.organizationNamePlaceholder',
                'Capilano Recreation'
              )
            "
            :state="fieldState('organization_name')"
          />
        </FormGroupBase>

        <FormGroupBase
          class="mb-3"
          :label="
            $t(
              'auth.identity.signupPage.organizationWebsiteLabel',
              'Organization website'
            )
          "
          :state="fieldState('website')"
          :invalid-feedback="fieldError('website')"
        >
          <InputUrl
            v-model="website"
            autocomplete="url"
            inputmode="url"
            :placeholder="
              $t(
                'auth.identity.signupPage.organizationWebsitePlaceholder',
                'https://example.org'
              )
            "
            :state="fieldState('website')"
          />
        </FormGroupBase>

        <FormGroupBase
          class="mb-3"
          :label="$t('auth.identity.signupPage.emailLabel', 'Email')"
          :state="fieldState('email')"
          :invalid-feedback="fieldError('email')"
        >
          <InputEmail
            v-model="email"
            autocomplete="email"
            :placeholder="
              $t(
                'auth.identity.signupPage.emailPlaceholder',
                'you{\'@\'}example.org'
              )
            "
            :state="fieldState('email')"
          />
        </FormGroupBase>

        <FormGroupBase
          :label="$t('auth.identity.signupPage.passwordLabel', 'Password')"
          :state="fieldState('password')"
          :invalid-feedback="fieldError('password')"
          class="signup-page__password"
        >
          <InputPassword
            v-model="password"
            autocomplete="new-password"
            :state="fieldState('password')"
          />
        </FormGroupBase>

        <button
          type="submit"
          class="btn-communal btn-communal--block"
          :disabled="submitting"
        >
          <span v-if="!submitting">
            {{
              $t(
                'auth.identity.signupPage.createMyAccount',
                'Create my account'
              )
            }}
            <span class="btn-communal__arrow" aria-hidden="true">→</span>
          </span>
          <span v-else>
            {{
              $t(
                'auth.identity.signupPage.creatingYourAccount',
                'Creating your account…'
              )
            }}
          </span>
        </button>
        <p class="signup-page__trust">
          {{
            $t(
              'auth.identity.signupPage.trustLine',
              '30-day free trial · No credit card · Cancel anytime'
            )
          }}
        </p>
      </FormBase>

      <p class="auth-foot-link">
        {{
          $t(
            'auth.identity.signupPage.alreadyHaveAccount',
            'Already have an account?'
          )
        }}
        <RouterLink to="/login">
          {{ $t('auth.identity.signupPage.signInInstead', 'Sign in instead') }}
        </RouterLink>
      </p>
    </div>

    <template #welcome>
      <div class="signup-welcome">
        <div class="signup-welcome__proof">
          <p class="signup-welcome__headline">
            {{ $t('auth.identity.signupPage.join', 'Join') }}
            <strong>{{
              $t('auth.identity.signupPage.organizationCount', '500+')
            }}</strong>
            {{
              $t(
                'auth.identity.signupPage.organizationsUsingCommunal',
                'organizations using Communal.'
              )
            }}
          </p>
          <p class="signup-welcome__sub">
            {{
              $t(
                'auth.identity.signupPage.communityCentersSub',
                'Community centers, associations, and programs run their operations here.'
              )
            }}
          </p>
        </div>

        <IdentityTestimonial
          quote="Experience with software and team has been overwhelmingly positive. We switched providers and the difference was night-and-day."
          initials="JN"
          author="Jon N."
          org="Tuscany Community Association"
        >
          <template #creds>
            <li
              class="testimonial__cred testimonial__cred--rating"
              :aria-label="
                $t(
                  'auth.identity.signupPage.ratedOnCapterra',
                  'Rated 4.7 on Capterra'
                )
              "
            >
              <IconStar solid class="testimonial__cred-star" />
              {{
                $t('auth.identity.signupPage.capterraRating', '4.7 on Capterra')
              }}
            </li>
          </template>
        </IdentityTestimonial>
      </div>
    </template>
  </IdentityShell>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';

import { FormBase, FormGroupBase } from '@/components/base/Form';
import { AuthSocialButtons } from '@/components/feature/Auth';
import {
  IdentityShell,
  IdentitySignupProgress,
  IdentityTestimonial,
} from '@/components/feature/Identity';
import { AlertDanger } from '@/components/ui/Alert';
import {
  InputEmail,
  InputPassword,
  InputText,
  InputUrl,
} from '@/components/ui/Form';
import { IconStar } from '@/components/ui/Icon';
import { useIdentityForm } from '@/composables/useIdentityForm';
import {
  TRIAL_SIGNUP_FAILED,
  TRIAL_SIGNUP_SUBMITTED,
  TRIAL_SIGNUP_VIEWED,
} from '@/lib/constants/funnelEvents';
import { trackEvent } from '@/lib/services/analytics/Measurement';
import { detectBrowserTimezone } from '@/lib/services/utils/BrowserTimezone';

const name = ref('');
const organizationName = ref('');
const website = ref('');
const email = ref('');
const password = ref('');

const googleAvailable = computed(() => Boolean(window.socialProviders?.google));

const { firstError, fieldState, fieldError, submitting, submit } =
  useIdentityForm({ url: '/signup' });

onMounted(() => {
  trackEvent(TRIAL_SIGNUP_VIEWED, { social_available: googleAvailable.value });
});

async function onSubmit() {
  trackEvent(TRIAL_SIGNUP_SUBMITTED, { signup_method: 'email' });

  const result = await submit({
    name: name.value,
    organization_name: organizationName.value,
    website: website.value,
    email: email.value,
    password: password.value,
    timezone: detectBrowserTimezone(),
  });

  if (!result.ok) {
    trackEvent(TRIAL_SIGNUP_FAILED, {
      signup_method: 'email',
      reason: result.failure,
    });
  }
}
</script>

<style lang="scss" scoped>
// Rendered inside IdentityShell's .communal-surface scope — raw <h1>, raw
// submit <button>, .section-headline, .section-eyebrow, .btn-communal, and
// .testimonial are deliberate marketing-surface classes, not in-product
// wrappers. See IdentityShell.vue for the full contract.

.signup-page__title {
  margin-bottom: 2rem;
}

.signup-page__social {
  margin-bottom: 1rem;
}

.signup-page__divider {
  margin: 0 0 1.25rem;
}

.signup-page__password {
  margin-bottom: 1rem;
}

.signup-page__trust {
  margin: 0.75rem 0 0;
  text-align: center;
  font-size: 0.8rem;
  font-weight: 500;
  color: var(--ink-500);
}

.signup-welcome {
  display: flex;
  flex-direction: column;
  gap: 1.625rem;
}

.signup-welcome__proof {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}

.signup-welcome__headline {
  margin: 0;
  font-size: 1.0625rem;
  font-weight: 600;
  line-height: 1.35;
  color: var(--ink-900);
  text-wrap: pretty;

  strong {
    font-weight: 700;
    font-variant-numeric: tabular-nums;
  }
}

.signup-welcome__sub {
  margin: 0;
  font-size: 0.875rem;
  line-height: 1.45;
  font-weight: 500;
  color: var(--ink-600);
  text-wrap: pretty;
}
</style>
